import { useQuery } from "@tanstack/react-query"
import { SectionHero, StatGrid, MetricCard, AdminCard, StatusPill } from "@/components/admin/primitives"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { supabase } from "@/integrations/supabase/client"
import { Brain, Clock, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Zap, Target } from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"

interface CoachingMetrics {
  totalRuns: number
  successfulCoaching: number
  failedCoaching: number
  avgResponseTime: number
  coachingRate: number
  recentFailures: Array<{
    id: string
    job_type: string
    error_code: string
    error_message: string
    created_at: string
  }>
}

export default function CoachingMonitor() {
  // Coaching metrics query
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery({
    queryKey: ["admin_coaching_metrics"],
    queryFn: async () => {
      // Get coaching statistics
      const { data: runs, error: runsError } = await supabase
        .from('swing_runs')
        .select('id, coaching_result, ai_result, created_at, updated_at')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      
      if (runsError) throw runsError
      
      // Get failed coaching jobs
      const { data: failedJobs, error: failedError } = await supabase
        .from('failed_jobs')
        .select('id, job_type, error_code, error_message, created_at')
        .eq('job_type', 'coaching_agent')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(10)
      
      if (failedError) throw failedError
      
      const totalRuns = runs.length
      const successfulCoaching = runs.filter(r => r.coaching_result !== null).length
      const failedCoaching = failedJobs.length
      
      // Calculate average response time (rough estimate based on created_at vs updated_at)
      const responseTimes = runs
        .filter(r => r.coaching_result !== null && r.updated_at)
        .map(r => new Date(r.updated_at).getTime() - new Date(r.created_at).getTime())
      
      const avgResponseTime = responseTimes.length > 0 
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length / 1000 // Convert to seconds
        : 0
      
      return {
        totalRuns,
        successfulCoaching,
        failedCoaching,
        avgResponseTime,
        coachingRate: totalRuns > 0 ? (successfulCoaching / totalRuns) * 100 : 0,
        recentFailures: failedJobs,
      } as CoachingMetrics
    },
    refetchInterval: 30000,
  })

  // Recent coaching results
  const { data: recentResults, isLoading: resultsLoading } = useQuery({
    queryKey: ["admin_recent_coaching"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('swing_runs')
        .select(`
          id,
          user_id,
          original_filename,
          coaching_result,
          created_at,
          updated_at,
          profiles:user_id (
            full_name,
            first_name,
            last_name
          )
        `)
        .not('coaching_result', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(20)
      
      if (error) throw error
      
      return data.map(run => ({
        ...run,
        coaching_summary: run.coaching_result?.summary || 'No summary available',
        assessments_count: run.coaching_result?.assessments?.length || 0,
        recommendations_count: run.coaching_result?.recommendations?.drills?.length || 0,
        confidence: run.coaching_result?.confidence || 0,
        user_name: run.profiles?.full_name || run.profiles?.first_name || 'Unknown User',
      }))
    },
    refetchInterval: 60000,
  })

  const getCoachingQuality = (confidence: number) => {
    if (confidence >= 0.8) return { label: 'High', color: 'success' }
    if (confidence >= 0.6) return { label: 'Medium', color: 'warning' }
    return { label: 'Low', color: 'danger' }
  }

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="AI Coaching Agent Monitor"
          description="Monitor OpenAI coaching agent performance, response times, and quality metrics"
          gradient="primary"
          accessory={
            <div className="flex items-center gap-2">
              <StatusPill 
                status={metrics && metrics.coachingRate >= 95 ? "healthy" : metrics && metrics.coachingRate >= 85 ? "degraded" : "critical"}
                label={`${metrics?.coachingRate.toFixed(1) ?? 0}% Success Rate`}
              />
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                <Brain className="h-3 w-3 mr-1" />
                AI Coaching Active
              </Badge>
            </div>
          }
          actions={
            <Button onClick={() => refetchMetrics()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          }
        />

        {/* Coaching Metrics */}
        <div className="mt-8">
          <h2 className="text-admin-section font-semibold text-foreground mb-4">Coaching Agent Performance (24h)</h2>
          {metricsLoading ? (
            <StatGrid columns={4}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : (
            <StatGrid columns={4}>
              <MetricCard
                title="Coaching Requests"
                value={metrics?.totalRuns.toLocaleString() ?? "0"}
                description="Total analysis requests"
                size="large"
              />
              <MetricCard
                title="Success Rate"
                value={`${metrics?.coachingRate.toFixed(1) ?? 0}%`}
                description={`${metrics?.successfulCoaching ?? 0} successful`}
                size="large"
                accentColor={metrics && metrics.coachingRate >= 95 ? "success" : metrics && metrics.coachingRate >= 85 ? "warning" : "danger"}
              />
              <MetricCard
                title="Avg Response Time"
                value={`${metrics?.avgResponseTime.toFixed(1) ?? 0}s`}
                description="OpenAI API response"
                size="large"
                accentColor={metrics && metrics.avgResponseTime < 30 ? "success" : metrics && metrics.avgResponseTime < 60 ? "warning" : "danger"}
              />
              <MetricCard
                title="Failed Jobs"
                value={metrics?.failedCoaching ?? 0}
                description="Requires attention"
                size="large"
                accentColor={metrics && metrics.failedCoaching === 0 ? "success" : "danger"}
              />
            </StatGrid>
          )}
        </div>

        {/* Recent Failures */}
        {metrics && metrics.recentFailures.length > 0 && (
          <div className="mt-8">
            <h2 className="text-admin-section font-semibold text-foreground mb-4">Recent Coaching Failures</h2>
            <div className="bg-admin-danger/5 border border-admin-danger/20 rounded-lg p-4">
              <div className="space-y-3">
                {metrics.recentFailures.slice(0, 5).map((failure) => (
                  <div key={failure.id} className="flex items-center justify-between py-2 border-b border-admin-danger/10 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-admin-danger/10">
                        <XCircle className="h-4 w-4 text-admin-danger" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{failure.error_code}</p>
                        <p className="text-xs text-muted-foreground max-w-md truncate">
                          {failure.error_message}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">
                        {new Date(failure.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-admin-danger/10">
                <Link to="/admin/debug">
                  <Button variant="outline" size="sm" className="text-admin-danger border-admin-danger/20 hover:bg-admin-danger/10">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Debug Coaching Issues
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Recent Coaching Results */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-admin-section font-semibold text-foreground">Recent Coaching Results</h2>
            <Badge variant="outline" className="text-xs">
              <Target className="h-3 w-3 mr-1" />
              Quality Metrics
            </Badge>
          </div>
          
          {resultsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {recentResults?.map((result) => {
                const quality = getCoachingQuality(result.confidence)
                return (
                  <AdminCard key={result.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-admin-success" />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {result.original_filename || `Analysis ${result.id.slice(0, 8)}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {result.user_name} • {new Date(result.updated_at).toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground max-w-md truncate">
                              {result.coaching_summary}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Assessments</p>
                          <p className="text-sm font-medium">{result.assessments_count}</p>
                        </div>
                        
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Drills</p>
                          <p className="text-sm font-medium">{result.recommendations_count}</p>
                        </div>
                        
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Confidence</p>
                          <div className="flex items-center gap-1">
                            <StatusPill 
                              status={quality.color as any} 
                              label={quality.label}
                            />
                            <span className="text-xs text-muted-foreground">
                              {(result.confidence * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            {new Date(result.updated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </AdminCard>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
